import {DataFrame, DataFrameRow, DeprecatedDataFrame} from 'data-frames/DataFrameTypes';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {AnalyticsVisualization} from 'insights/analytics-visualizations/__generated/AnalyticsVisualization';
import {VisualizationType} from 'insights/analytics-visualizations/AnalyticsVisualizationTypes';
import {
  HeatmapConfig,
  HeatmapData,
  HeatmapPoint,
  HEATMAP_TYPE_VALIDATION_MAP,
  HeatmapProps,
} from 'ui-library/charting/Heatmap';

import cleanAndValidateDataFrame from './cleanAndValidateDataFrame';
import convertDataFrameToLegacyChartData from './convertDataFrameToLegacyChartData';
import maybeConvertProps from './maybeConvertProps';
import {ColumnInfo, ConversionOutput, SuccessfulConversionOutput} from './PropConverterTypes';
import findAxisColumn from './utils/findAxisColumn';

type HeatmapParameters = ExtractByTypename<
  AnalyticsVisualization['parameters'],
  'AnalyticsVisualizationHeatmapParametersNode'
>;

export function getHeatmapColumnInfos(parameters: HeatmapParameters): Array<ColumnInfo> {
  return [
    {
      chartDataColumnName: 'x',
      inputDataColumnSystemName: parameters.xAxisColumnName,
      typeValidationOptions: HEATMAP_TYPE_VALIDATION_MAP.x,
      cleanupNulls: true,
    },
    {
      chartDataColumnName: 'y',
      inputDataColumnSystemName: parameters.yAxisColumnName,
      typeValidationOptions: HEATMAP_TYPE_VALIDATION_MAP.y,
      cleanupNulls: true,
    },
    {
      chartDataColumnName: 'metric',
      inputDataColumnSystemName: parameters.metricColumnName,
      typeValidationOptions: HEATMAP_TYPE_VALIDATION_MAP.metric,
      cleanupNulls: false,
    },
  ];
}

export function cleanAndValidateHeatmapData(dataFrame: DataFrame, parameters: HeatmapParameters): DataFrame {
  const columnInfos = getHeatmapColumnInfos(parameters);
  return cleanAndValidateDataFrame(dataFrame, columnInfos, VisualizationType.HEATMAP);
}

/**
 * @deprecated TODO(BNCH-24546): Delete convertDeprecatedHeatmapData and use cleanAndValidateHeatmapData directly
 * once heatmap takes a DataFrame.
 */
export function convertDeprecatedHeatmapData(
  dataFrame: DataFrame,
  parameters: HeatmapParameters
): HeatmapData<DataFrameRow & HeatmapPoint> {
  const cleanDataFrame = cleanAndValidateHeatmapData(dataFrame, parameters);

  const columnInfos = getHeatmapColumnInfos(parameters);

  return convertDataFrameToLegacyChartData(cleanDataFrame, columnInfos) as DeprecatedDataFrame<
    DataFrameRow & HeatmapPoint
  >;
}

export function convertHeatmapConfig(dataFrame: DataFrame, parameters: HeatmapParameters): HeatmapConfig {
  const metricColumn = findAxisColumn(dataFrame, parameters.metricColumnName);
  const legend = {label: metricColumn.displayName};
  const ascendingOrder = parameters.gradientDirection === 'ASCENDING_METRIC_VALUES';
  return {
    legend,
    ascendingOrder,
  };
}

export function convertHeatmapProps(
  dataFrame: DataFrame,
  parameters: HeatmapParameters
): SuccessfulConversionOutput<HeatmapProps<HeatmapPoint>> {
  return {
    valid: true,
    props: {
      data: convertDeprecatedHeatmapData(dataFrame, parameters),
      config: convertHeatmapConfig(dataFrame, parameters),
    },
  };
}

export default function maybeConvertHeatmapProps(
  dataFrame: DataFrame,
  parameters: HeatmapParameters
): ConversionOutput<HeatmapProps<HeatmapPoint>> {
  return maybeConvertProps(() => convertHeatmapProps(dataFrame, parameters));
}
